import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Grid,
  Typography,
  Alert,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import ControlledTextInputField from "../../component/ui/FormFields/ControlledTextInputField";
import { useSelector } from "react-redux";
import CustomButton from "../../component/ui/FormFields/Button";
import { useDispatch } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  BOOKING_PASSENGER_DETAILS,
  BOOKINGS
} from "../../component/common/Routes/AppRoutes";
import {
  VENDOR_BOOKING_PASSENGER_DETAILS,
  VENDOR_BOOKINGS
} from "../../component/common/Routes/VendorRoutes";
import {
  CUSTOMER_BOOKING_PASSENGER_DETAILS,
  CUSTOMER_BOOKINGS
} from "../../component/common/Routes/CustomerRoutes";
import {
  editBookingRequest,
  fetchBookingRequest,
  resetBookingDetails
} from "../../redux/Bookings/BookingAction";
import "../../styles/UserStyles.css";
import Loading from "../../component/ui/Loading";

const EditBooking = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const bookingDetail = useSelector((state) => state.booking?.instantBooking);
  const instantBookingLoading = bookingDetail?.loading;
  const success = bookingDetail?.success;
  const error = bookingDetail?.error;

  const loading = useSelector((state) => state.booking?.loading);
  const bookingData = location.state?.bookingData || {};
  const { bookingId } = useParams();
  const [applyPenalty, setApplyPenalty] = useState(false);
  const [penaltyAmount, setPenaltyAmount] = useState("");

  const [penaltyError, setPenaltyError] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [updatedBookingData, setUpdatedBookingData] = useState(null);

  useEffect(() => {
    dispatch(fetchBookingRequest(bookingId));
    dispatch(resetBookingDetails());
  }, [dispatch, bookingId]);

  const bookingDetails = useSelector((state) => state.booking?.booking?.data);
  const user = useSelector((state) => state.auth?.user);
  const isIntantBooking = false;

  const {
    formState: { errors },
    control,
    handleSubmit,
    setValue,
    watch
  } = useForm({
    defaultValues: {
      departure_date: bookingDetails?.BookingDetail?.date_of_departure || null,
      departure_time: bookingDetails?.BookingDetail?.time_of_departure || "",
      arrival_date: bookingDetails?.BookingDetail?.date_of_arrival || null,
      arrival_time: bookingDetails?.BookingDetail?.time_of_arrival || "",
      arrival_flight_number:
        bookingDetails?.BookingDetail?.arrival_flight_number || "",
      departure_flight_number:
        bookingDetails?.BookingDetail?.departure_flight_number || ""
    },
    mode: "onChange"
  });

  useEffect(() => {
    if (bookingDetails?.BookingDetail) {
      const details = bookingDetails.BookingDetail;

      setValue("departure_date", details.date_of_departure);
      setValue("departure_time", details.time_of_departure);
      setValue("arrival_date", details.date_of_arrival);
      setValue("arrival_time", details.time_of_arrival);
      setValue("arrival_flight_number", details.arrival_flight_number);
      setValue("departure_flight_number", details.departure_flight_number);
    }
  }, [bookingDetails, setValue]);

  const formValues = watch();

  const handlePenaltyAmountChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d+(\.\d{0,2})?$/.test(value)) {
      setPenaltyAmount(value);
      setPenaltyError("");
    }
  };

  const handleApplyPenaltyChange = (e) => {
    setApplyPenalty(e.target.checked);
    if (!e.target.checked) {
      setPenaltyAmount("");

      setPenaltyError("");
    }
  };

  const serviceType = bookingData.service_type_label || "";

  const arrivalFields = [
    {
      name: "arrival_flight_number",
      label: "Arrival Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 12
    },
    {
      name: "arrival_date",
      label: "Arrival Date *",
      placeholder: "",
      type: "datebooking",
      instantBooking: isIntantBooking,
      sm: 6,
      rules: {
        required: "Date of Arrival is required",
        validate: (value) => {
          if (!value) return "Date is required";

          const selectedDate = new Date(value).setHours(0, 0, 0, 0);
          const today = new Date().setHours(0, 0, 0, 0);

          return selectedDate >= today || "Date cannot be in the past";
        }
      }
    },
    {
      name: "arrival_time",
      label: "Arrival Time *",
      placeholder: "",
      type: "time",
      sm: 6,
      rules: {
        required: "Time of Arrival is required",
        validate: (_, formValues) => {
          const { arrival_date, arrival_time } = formValues;

          if (!arrival_date || !arrival_time)
            return "Date and Time are required";

          const selectedDateTime = new Date(`${arrival_date}T${arrival_time}`);
          const pastTimeCheck = new Date();

          return selectedDateTime >= pastTimeCheck || "Future Time Must Select";
        }
      }
    }
  ];

  const departureFields = [
    {
      name: "departure_flight_number",
      label: "Departure Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 12
    },
    {
      name: "departure_date",
      label: "Departure Date *",
      placeholder: "",
      type: "datebooking",
      sm: 6,
      instantBooking: isIntantBooking,
      rules: {
        required: "Date of Departure is required",
        validate: (value) => {
          if (!value) return "Date is required";
          const selectedDate = new Date(value).setHours(0, 0, 0, 0);
          const today = new Date().setHours(0, 0, 0, 0);
          return selectedDate >= today || "Date cannot be in the past";
        }
      }
    },
    {
      name: "departure_time",
      label: "Departure Time *",
      placeholder: "",
      rules: {
        required: "Time of Departure is required",
        validate: (_, formValues) => {
          const { departure_date, departure_time } = formValues;

          if (!departure_date || !departure_time)
            return "Date and Time are required";

          const selectedDateTime = new Date(
            `${departure_date}T${departure_time}`
          );
          const pastTimeCheck = new Date();

          return selectedDateTime >= pastTimeCheck || "Future Time Must Select";
        }
      },
      type: "time",
      sm: 6
    }
  ];

  const transitFields = [
    {
      name: "arrival_flight_number",
      label: "Arrival Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 6
    },
    {
      name: "departure_flight_number",
      label: "Departure Flight Number *",
      placeholder: "",
      rules: { required: "Flight Number is required" },
      type: "text",
      sm: 6
    },
    {
      name: "arrival_date",
      label: "Arrival Date *",
      placeholder: "",
      instantBooking: isIntantBooking,
      rules: {
        required: "Date of Arrival is required",
        validate: (value) => {
          if (!value) return "Date is required";
          const selectedDate = new Date(value).setHours(0, 0, 0, 0);
          const today = new Date().setHours(0, 0, 0, 0);
          return selectedDate >= today || "Date cannot be in the past";
        }
      },
      type: "datebooking",
      sm: 6
    },
    {
      name: "departure_date",
      label: "Departure Date *",
      placeholder: "",
      instantBooking: isIntantBooking,
      rules: {
        required: "Date of Departure is required",
        validate: (value) => {
          if (!value) return "Date is required";
          const selectedDate = new Date(value).setHours(0, 0, 0, 0);
          const today = new Date().setHours(0, 0, 0, 0);
          return selectedDate >= today || "Date cannot be in the past";
        }
      },
      type: "datebooking",
      sm: 6
    },
    {
      name: "arrival_time",
      label: "Arrival Time *",
      placeholder: "",
      type: "time",
      sm: 6,
      rules: {
        required: "Time of Arrival is required",
        validate: (_, formValues) => {
          const { arrival_date, arrival_time } = formValues;

          if (!arrival_date || !arrival_time)
            return "Date and Time are required";

          const selectedDateTime = new Date(`${arrival_date}T${arrival_time}`);
          const pastTimeCheck = new Date();

          return selectedDateTime >= pastTimeCheck || "Future Time Must Select";
        }
      }
    },
    {
      name: "departure_time",
      label: "Departure Time *",
      placeholder: "",
      rules: {
        required: "Time of Departure is required",
        validate: (_, formValues) => {
          const { departure_date, departure_time } = formValues;

          if (!departure_date || !departure_time)
            return "Date and Time are required";

          const selectedDateTime = new Date(
            `${departure_date}T${departure_time}`
          );
          const pastTimeCheck = new Date();

          return selectedDateTime >= pastTimeCheck || "Future Time Must Select";
        }
      },
      type: "time",
      sm: 6
    }
  ];

  const getFields = () => {
    if (serviceType === "Arrival") {
      return arrivalFields;
    } else if (serviceType === "Departure") {
      return departureFields;
    } else if (serviceType === "Transit") {
      return transitFields;
    }
  };

  const getRoute = () => {
    if (user?.role === "Admin" || user?.role === "Staff") {
      return BOOKINGS;
    } else if (user?.role === "Vendor") {
      return VENDOR_BOOKINGS;
    } else if (user?.role === "Customer") {
      return CUSTOMER_BOOKINGS;
    }
    return BOOKINGS;
  };

  const validatePenalty = () => {
    if (applyPenalty) {
      if (!penaltyAmount) {
        setPenaltyError("Penalty amount is required");
        return false;
      }

      if (parseFloat(penaltyAmount) <= 0) {
        setPenaltyError("Penalty amount must be greater than 0");
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (data) => {
    if (!validatePenalty()) {
      return;
    }
    const updatedBooking = {
      date_of_departure: data.departure_date || null,
      time_of_departure: data.departure_time || "1",
      date_of_arrival: data.arrival_date || null,
      time_of_arrival: data.arrival_time || "1",
      departure_flight_number: data.departure_flight_number || null,
      arrival_flight_number: data.arrival_flight_number || null,
      penalty_charge: applyPenalty ? parseFloat(penaltyAmount) : "0",
      date_of_transit: null,
      time_of_transit: null
    };

    dispatch(editBookingRequest({ bookingId, updatedData: updatedBooking }));
  };

  useEffect(() => {
    if (error) {
      toast.error(error, {
        position: "top-center",
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: false
      });
    }

    if (success) {
      toast.success("Booking Updated successfully!", {
        position: "top-center",
        autoClose: 5000,
        closeOnClick: true,
        pauseOnHover: false
      });

      const timer = setTimeout(() => {
        navigate(getRoute());
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [error, success, navigate]);

  const saveBooking = (bookingData) => {
    setShowConfirmation(false);

    navigate(getRoute());
  };

  const handleConfirmPayment = () => {
    if (updatedBookingData) {
      saveBooking(updatedBookingData);
    }
  };

  if (loading) {
    return <Loading text="Loading Booking details..." />;
  }
  const canApplyPenalty = user?.role === "Admin" || user?.role === "Staff";

  return (
    <>
      <ToastContainer />
      <Box className="container">
        <Box component={Paper} className="add_airport_details_heading">
          <Typography variant="h5" className="form-heading">
            Edit Booking
          </Typography>
          <Box className="form-margin">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={4}>
                {getFields().map((field, index) => (
                  <Grid item xs={12} sm={field.sm} key={index}>
                    <ControlledTextInputField
                      name={field.name}
                      control={control}
                      rules={field.rules}
                      type={field.type}
                      instantBooking={field.instantBooking}
                      label={field.label}
                      placeholder={field.placeholder}
                      error={!!errors[field.name]}
                      helperText={errors[field.name]?.message}
                      setValue={setValue}
                    />
                  </Grid>
                ))}

                {/* Penalty Section - only visible to admin/staff */}
                {canApplyPenalty && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        padding: 2
                      }}
                    >
                      <Typography variant="h6" gutterBottom>
                        Penalty Information
                      </Typography>

                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={applyPenalty}
                            onChange={handleApplyPenaltyChange}
                            color="primary"
                          />
                        }
                        label="Apply Penalty"
                      />

                      {applyPenalty && (
                        <Grid container spacing={2} sx={{ marginTop: "5px" }}>
                          <Grid item xs={12}>
                            <TextField
                              label="Penalty Amount ($)"
                              fullWidth
                              value={penaltyAmount}
                              onChange={handlePenaltyAmountChange}
                              error={!!penaltyError}
                              helperText={penaltyError}
                              placeholder="0.00"
                              InputProps={{
                                startAdornment: (
                                  <Box component="span" sx={{ mr: 1 }}>
                                    ₹
                                  </Box>
                                )
                              }}
                            />
                          </Grid>
                        </Grid>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={12} className="button-container">
                  <CustomButton
                    variant="contained"
                    className="grey-button"
                    text="Cancel"
                    onClick={() => navigate(getRoute())}
                  />
                  <CustomButton
                    variant="contained"
                    className="orange-button"
                    text="Save Changes"
                    type="submit"
                    loading={instantBookingLoading}
                  />
                </Grid>
              </Grid>
            </form>
          </Box>
        </Box>

        <Dialog
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Confirm Changes with Penalty</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                A penalty has been applied to this booking
              </Alert>

              <Typography variant="body1" gutterBottom>
                <strong>Penalty Amount:</strong> ₹
                {parseFloat(penaltyAmount).toFixed(2)}
              </Typography>

              <Typography
                variant="body2"
                sx={{ mt: 2, color: "text.secondary" }}
              >
                Please confirm these changes and proceed with payment
                processing.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <CustomButton
              variant="contained"
              className="grey-button"
              text="Cancel"
              onClick={() => setShowConfirmation(false)}
            />
            <CustomButton
              variant="contained"
              className="orange-button"
              text="Confirm & Pay"
              onClick={handleConfirmPayment}
              loading={loading}
            />
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default EditBooking;
